import { ColumnDef } from '@tanstack/react-table'
import { Order, OrderProduct, OrderStatus } from '@/types'
import { format, differenceInDays } from 'date-fns'
import { de } from 'date-fns/locale'

const formatDate = (date: Date) => format(date, 'dd.MM.yyyy', { locale: de })
const formatCurrency = (amount: number) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount)
const formatDelay = (actual: Date, confirmed: Date) => {
  const days = differenceInDays(actual, confirmed)
  return `+${days} day${days > 1 ? 's' : ''}`
}

const statusOrder: OrderStatus[] = [
  'rfq',
  'quoted',
  'in_draft',
  'pending_confirmation',
  'confirmed',
  'in_production',
  'final_testing',
  'ready_for_shipping',
  'in_delivery',
  'completed',
  'cancelled'
]

export const statusColors: Record<OrderStatus, { selected: string; unselected: string }> = {
  rfq: {
    selected: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-100',
    unselected: 'bg-gray-50/50 text-gray-600 dark:bg-gray-950 dark:text-gray-500'
  },
  quoted: {
    selected: 'bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-100',
    unselected: 'bg-blue-50/50 text-blue-600 dark:bg-blue-950 dark:text-blue-500'
  },
  in_draft: {
    selected: 'bg-purple-100 text-purple-800 dark:bg-purple-800 dark:text-purple-100',
    unselected: 'bg-purple-50/50 text-purple-600 dark:bg-purple-950 dark:text-purple-500'
  },
  pending_confirmation: {
    selected: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-100',
    unselected: 'bg-yellow-50/50 text-yellow-600 dark:bg-yellow-950 dark:text-yellow-500'
  },
  confirmed: {
    selected: 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100',
    unselected: 'bg-green-50/50 text-green-600 dark:bg-green-950 dark:text-green-500'
  },
  in_production: {
    selected: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-800 dark:text-indigo-100',
    unselected: 'bg-indigo-50/50 text-indigo-600 dark:bg-indigo-950 dark:text-indigo-500'
  },
  final_testing: {
    selected: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-800 dark:text-cyan-100',
    unselected: 'bg-cyan-50/50 text-cyan-600 dark:bg-cyan-950 dark:text-cyan-500'
  },
  ready_for_shipping: {
    selected: 'bg-orange-100 text-orange-800 dark:bg-orange-800 dark:text-orange-100',
    unselected: 'bg-orange-50/50 text-orange-600 dark:bg-orange-950 dark:text-orange-500'
  },
  in_delivery: {
    selected: 'bg-pink-100 text-pink-800 dark:bg-pink-800 dark:text-pink-100',
    unselected: 'bg-pink-50/50 text-pink-600 dark:bg-pink-950 dark:text-pink-500'
  },
  completed: {
    selected: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-800 dark:text-emerald-100',
    unselected: 'bg-emerald-50/50 text-emerald-600 dark:bg-emerald-950 dark:text-emerald-500'
  },
  cancelled: {
    selected: 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100',
    unselected: 'bg-red-50/50 text-red-600 dark:bg-red-950 dark:text-red-500'
  }
} as const;

export const orderColumns: ColumnDef<Order>[] = [
  {
    accessorKey: 'orderNumber',
    header: 'Order #',
    enableSorting: true,
    sortingFn: 'basic',
    cell: ({ row }) => (
      <div className="font-medium text-gray-900 dark:text-gray-100">
        {row.original.orderNumber}
      </div>
    ),
  },
  {
    accessorKey: 'orderDate',
    header: 'Ordered',
    enableSorting: true,
    sortingFn: (rowA, rowB) => {
      const a = rowA.getValue('orderDate') as Date
      const b = rowB.getValue('orderDate') as Date
      return a.getTime() - b.getTime()
    },
    cell: ({ row }) => (
      <div className="text-sm text-gray-500 dark:text-gray-400">
        {formatDate(row.original.orderDate)}
      </div>
    ),
  },
  {
    accessorKey: 'status',
    header: 'Status',
    enableSorting: true,
    sortingFn: (rowA, rowB) => {
      const a = rowA.getValue('status') as OrderStatus
      const b = rowB.getValue('status') as OrderStatus
      return statusOrder.indexOf(a) - statusOrder.indexOf(b)
    },
    filterFn: (row, columnId, filterValue: string[]) => {
      const status = row.getValue(columnId) as string
      return filterValue.includes(status)
    },
    cell: ({ row }) => {
      const status = row.original.status;
      if (!statusColors[status]) {
        return (
          <div className="space-y-1">
            <div className="flex gap-2">
              <span className="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-100">
                {status.replace(/_/g, ' ')}
              </span>
            </div>
          </div>
        );
      }
      return (
        <div className="space-y-1">
          <div className="flex gap-2">
            <span className={`px-2 py-1 rounded-full text-xs ${statusColors[status].selected}`}>
              {status.replace(/_/g, ' ')}
            </span>
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: 'requestedDeliveryDate',
    header: 'Requested',
    enableSorting: true,
    sortingFn: (rowA, rowB) => {
      const a = rowA.getValue('requestedDeliveryDate') as Date | null
      const b = rowB.getValue('requestedDeliveryDate') as Date | null
      if (a === null && b === null) return 0
      if (a === null) return 1
      if (b === null) return -1
      return a.getTime() - b.getTime()
    },
    cell: ({ row }) => {
      const requested = row.original.requestedDeliveryDate;
      return (
        <div className="text-sm text-gray-900 dark:text-gray-100">
          {requested ? formatDate(requested) : '-'}
        </div>
      );
    },
  },
  {
    accessorKey: 'confirmedDeliveryDate',
    header: 'Confirmed',
    enableSorting: true,
    sortingFn: (rowA, rowB) => {
      const a = rowA.getValue('confirmedDeliveryDate') as Date | null
      const b = rowB.getValue('confirmedDeliveryDate') as Date | null
      if (a === null && b === null) return 0
      if (a === null) return 1
      if (b === null) return -1
      return a.getTime() - b.getTime()
    },
    cell: ({ row }) => {
      const confirmed = row.original.confirmedDeliveryDate;
      return (
        <div className="text-sm text-gray-900 dark:text-gray-100">
          {confirmed ? formatDate(confirmed) : '-'}
        </div>
      );
    },
  },
  {
    accessorKey: 'revisedDeliveryDate',
    header: 'Revised',
    enableSorting: true,
    sortingFn: (rowA, rowB) => {
      const a = rowA.getValue('revisedDeliveryDate') as Date | null
      const b = rowB.getValue('revisedDeliveryDate') as Date | null
      if (a === null && b === null) return 0
      if (a === null) return 1
      if (b === null) return -1
      return a.getTime() - b.getTime()
    },
    cell: ({ row }) => {
      const revised = row.original.revisedDeliveryDate;
      return (
        <div className="text-sm text-gray-900 dark:text-gray-100">
          {revised ? formatDate(revised) : '-'}
        </div>
      );
    },
  },
  {
    accessorKey: 'actualDeliveryDate',
    header: 'Actual',
    enableSorting: true,
    sortingFn: (rowA, rowB) => {
      const a = rowA.getValue('actualDeliveryDate') as Date | null
      const b = rowB.getValue('actualDeliveryDate') as Date | null
      if (a === null && b === null) return 0
      if (a === null) return 1
      if (b === null) return -1
      return a.getTime() - b.getTime()
    },
    cell: ({ row }) => {
      const actual = row.original.actualDeliveryDate;
      const status = row.original.status;
      if (!actual || ['in_production', 'in_delivery'].includes(status)) {
        return <span className="text-gray-500 dark:text-gray-400">-</span>;
      }
      
      const confirmed = row.original.confirmedDeliveryDate;
      const isDelayed = confirmed && actual > confirmed;
      const delay = isDelayed && confirmed ? formatDelay(actual, confirmed) : null;

      return (
        <div>
          <div className={`text-sm ${isDelayed ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-gray-100'}`}>
            {formatDate(actual)}
          </div>
          {delay && (
            <div className="text-xs text-red-500 dark:text-red-400">
              {delay} after confirmed
            </div>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: 'totalAmount',
    header: 'Total',
    enableSorting: true,
    sortingFn: 'basic',
    cell: ({ row }) => (
      <div className="text-right font-medium text-gray-900 dark:text-gray-100">
        {formatCurrency(row.original.totalAmount)}
      </div>
    ),
  },
];

export const productDetailsColumns: ColumnDef<OrderProduct>[] = [
  {
    accessorKey: 'name',
    header: 'Product',
    cell: ({ row }) => (
      <div>
        <div className="font-medium text-gray-900 dark:text-gray-100">{row.original.name}</div>
        <div className="text-xs text-gray-500 dark:text-gray-400">{row.original.category.replace(/_/g, ' ')}</div>
      </div>
    ),
  },
  {
    accessorKey: 'quantity',
    header: 'Quantity',
    cell: ({ row }) => (
      <div className="text-gray-900 dark:text-gray-100">
        {new Intl.NumberFormat('de-DE').format(row.original.quantity)}
      </div>
    ),
  },
  {
    accessorKey: 'unitPrice',
    header: 'Unit Price',
    cell: ({ row }) => (
      <div className="text-gray-900 dark:text-gray-100">
        {formatCurrency(row.original.unitPrice)}
      </div>
    ),
  },
  {
    accessorKey: 'leadTime',
    header: 'Lead Time',
    cell: ({ row }) => (
      <div className="text-gray-900 dark:text-gray-100">
        {new Intl.NumberFormat('de-DE').format(row.original.leadTime)} days
      </div>
    ),
  },
  {
    accessorKey: 'specifications',
    header: 'Details',
    cell: ({ row }) => (
      <div>
        {row.original.isCustom && (
          <>
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400">Drawing: {row.original.drawingNumber}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-xs" title={row.original.specifications}>
              {row.original.specifications}
            </div>
          </>
        )}
      </div>
    ),
  },
];